<script lang="ts">
    import { page } from '$app/stores';
    import { stripGID } from '$lib/utils/functions/stripGID';

    export let options;
    export let variants;
    export let selectedVariant;

    // Function that sorts strings prefixed with digits
    const numericOptionSorter = (a, b) => {
        let int1 = a.match(/\d+/);
        let int2 = b.match(/\d+/);
        if (int1 && int2) {
            return int1[0] - int2[0];
        }
        return 0;
    };

    // Resort the options, and modify the value object availability/name
    options = options.map((option) => {
        let values = option.values
            .map((value) => ({
                name: value
            }))
            .sort((a, b) => numericOptionSorter(a.name, b.name));
        return { ...option, values };
    });

    // Resort our variants;
    variants = variants.nodes.slice().sort((itm1, itm2) => {
        function recursiveSort(idx = 0) {
            if (itm1.selectedOptions[idx].value === itm2.selectedOptions[idx].value) {
                return recursiveSort(idx + 1);
            }
            return numericOptionSorter(itm1.selectedOptions[idx].value, itm2.selectedOptions[idx].value);
        }

        return recursiveSort();
    });

    // Function to remap the options with availability based on selections
    const optionAvailabilityBuilder = (selectedOption) => {
        // sub-function to get availability values, called from the map at bottom of optionBuilder
        const getAvailableOnTree = (optionsIndex, optionValue) => {
            // Destructure the value name eg `1pk' from optionValue argument
            const { name: optionName } = optionValue;

            // Filter all variants based on prior option selections and then check the current iterated map argument
            let result = variants.filter((variant) => {
                let match = true;
                // Only work on prior options
                if (optionsIndex > 0) {
                    let i = 0;
                    while (i < optionsIndex && match) {
                        match = variant.selectedOptions[i].value === Object.values(selectedOption)[i];
                        i++;
                    }
                }
                // Now if the priors match and the filter matches the mapping iterator we can return it
                return match && variant.selectedOptions[optionsIndex].value === optionName;
            });

            // Use a reducer to sum availability number for this option path
            const stockAvailableOnThisTree = result.reduce((accumulator, variant) => {
                return accumulator + variant.quantityAvailable;
            }, 0);

            return stockAvailableOnThisTree;
        };

        // Return a new map of options and values
        return options.map((option, optionsIndex) => {
            // Rebuild this option's values with their new availabilities
            const values = option.values.map((optionValue) => {
                let available = getAvailableOnTree(optionsIndex, optionValue);
                return { ...optionValue, available };
            });
            return { ...option, values }; // Return the original option, with new values array
        });
    };

    // Function to find variant that matches the selectedOptions, otherwise first available variant, otherwise first variant
    const findVariant = (selectedOption = {}) => {
        let [matchedVariant] = Object.values(selectedOption).reduce((accumulator, selectedOptionValue, selectedOptionIndex) => {
            // First iteration needs to filter the variants array directly because this shitter won't let us pass 'variants' as the constructor for the reducer
            if (selectedOptionIndex === 0) return variants.filter((variant) => variant.selectedOptions[selectedOptionIndex].value === selectedOptionValue);
            // All other iterations work directly on the accumulator
            return accumulator.filter((remainingVariants) => remainingVariants.selectedOptions[selectedOptionIndex].value === selectedOptionValue);
        }, []);

        return matchedVariant || variants.find((variant) => variant.quantityAvailable > 0) || variants[0] || {};
    };

    // Now initialize all the reactive bits, order of this stuff matters to stop circular dependancies/breaking tings.
    //
    selectedVariant = findVariant(); // set the variant on initialization (First avail/first)
    // Then define selected options from the initial selectedVariant (bound to our selector components)
    let selectedOption = selectedVariant.selectedOptions.reduce((acc, option) => ({ ...acc, [option['name']]: option.value }), {}) || {};

    $: selectedVariant = findVariant(selectedOption); // Now, Reactivelty set selected variant on option changes
    $: options = optionAvailabilityBuilder(selectedOption); // remap the options availability based on selections
</script>

{#each options as option}
    <div class="py-3.5" class:hidden={option.name === 'Hidden' || (option.name === 'Size' && variants?.[0]?.sku?.startsWith('ELQ')) || option.name === 'Title'}>
        <div class=" text-sm">{option.name}</div>
        {#each option.values as value (value)}
            {#key $page}
                <input
                    class="hidden"
                    type="radio"
                    id={`${option.name}-${value.name}-${stripGID(option.id)}`}
                    name={`${option.name}`}
                    value={value.name}
                    bind:group={selectedOption[option.name]}
                />
                <label
                    for={`${option.name}-${value.name}-${stripGID(option.id)}`}
                    class={`my-2 mr-2 inline-block cursor-pointer rounded-full border-2 px-4 py-1.5 text-sm
                        ${selectedOption[option.name] === value.name && value.available > 0 ? 'border-primary bg-primary text-white' : 'border-color-text'} 
                        ${value.available > 0 ? ' text-color-text' : 'border-color-text/20 bg-transparent text-color-text/20'}`}
                >
                    {value.name}
                </label>
            {/key}
        {/each}
    </div>
{/each}
